import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { usePostsStore } from '../stores/postsStore';

const PostsCalendarSearch = ({ profileId }) => {
  const { accessToken } = useAuthStore();
  const {
    calendarDates,
    searchResults,
    selectedDate,
    selectedPostId,
    fetchCalendarData,
    searchPosts,
    clearSearch,
    jumpToDate,
    jumpToPost,
    resetToLatest,
  } = usePostsStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Reset calendar to current month when timeline is reset
  useEffect(() => {
    if (!selectedDate && !selectedPostId) {
      setCurrentMonth(new Date());
    }
  }, [selectedDate, selectedPostId]);

  // Fetch calendar data on mount
  useEffect(() => {
    if (accessToken && profileId) {
      fetchCalendarData(accessToken, profileId);
    }
  }, [accessToken, profileId]);

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push({ date: null, count: 0 });
    }

    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dateData = calendarDates.find(d => d.date === dateStr);
      days.push({
        date: day,
        count: dateData ? parseInt(dateData.count) : 0,
        dateStr: dateStr,
        isToday: dateStr === todayStr,
        isSelected: dateStr === selectedDate
      });
    }

    return days;
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim().length >= 2 && accessToken && profileId) {
      searchPosts(accessToken, searchQuery, profileId);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    clearSearch();
  };

  const handleSearchResultClick = async (result) => {
    console.log('Search result clicked:', result);
    if (!accessToken || !profileId) return;
    const isReply = result.type === 'reply';
    console.log('Calling jumpToPost with postId:', result.id, 'isReply:', isReply);
    await jumpToPost(accessToken, result.id, profileId, isReply);
  };

  // Handle calendar date click
  const handleDateClick = async (dateStr) => {
    console.log('Calendar date clicked:', dateStr);
    if (!accessToken || !profileId) return;
    console.log('Calling jumpToDate with:', { accessToken: !!accessToken, dateStr, profileId });
    await jumpToDate(accessToken, dateStr, profileId);
    console.log('After jumpToDate, selectedDate should be:', dateStr);
  };

  // Handle back to top (not used in this component anymore, handled by Dashboard)
  // const handleBackToTop = async () => {
  //   if (!accessToken || !profileId) return;
  //   setCurrentMonth(new Date()); // Reset calendar to current month
  //   await resetToLatest(accessToken, profileId);
  // };

  // Extract plain text from HTML
  const stripHTML = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const calendarDays = generateCalendarDays();

  return (
    <div className="space-y-6">
      {/* Calendar */}
      <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            ←
          </button>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
            {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </h3>
          <button
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            →
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-xs">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center font-semibold text-gray-600 dark:text-gray-400 pb-1">
              {day}
            </div>
          ))}
          {calendarDays.map((day, idx) => {
            let className = 'p-1 text-center rounded ';

            if (day.date) {
              if (day.isSelected) {
                // Selected date - bright blue
                className += 'bg-primary-600 dark:bg-primary-600 text-white font-bold border-2 border-primary-800 dark:border-primary-400';
              } else if (day.isToday) {
                // Today - blue outline
                className += 'border-2 border-blue-500 dark:border-blue-400 font-semibold text-gray-900 dark:text-gray-100';
                if (day.count > 0) {
                  className += ' bg-primary-100 dark:bg-primary-900 cursor-pointer hover:bg-primary-200 dark:hover:bg-primary-800';
                } else {
                  className += ' text-gray-600 dark:text-gray-400';
                }
              } else if (day.count > 0) {
                // Has posts - blue background
                className += 'bg-primary-100 dark:bg-primary-900 cursor-pointer hover:bg-primary-200 dark:hover:bg-primary-800 font-semibold text-gray-900 dark:text-gray-100';
              } else {
                // Normal day
                className += 'text-gray-600 dark:text-gray-400';
              }
            }

            return (
              <div
                key={idx}
                className={className}
                title={day.count > 0 ? `${day.count} post(s)` : ''}
                onClick={() => day.count > 0 && handleDateClick(day.dateStr)}
              >
                {day.date || ''}
              </div>
            );
          })}
        </div>
      </div>

      {/* Search */}
      <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-800">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Search</h3>
        <form onSubmit={handleSearch} className="space-y-2">
          <div className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search posts..."
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
            />
            <button
              type="submit"
              disabled={searchQuery.trim().length < 2}
              className="p-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Search"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </div>
          {searchResults.length > 0 && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="w-full px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
            >
              Clear Search
            </button>
          )}
        </form>

        {/* Search Results List */}
        {searchResults.length > 0 && (
          <div className="mt-4 max-h-96 overflow-y-auto space-y-2">
            {searchResults.map((result) => {
              const plainText = stripHTML(result.body);
              const snippet = plainText.length > 100 ? plainText.substring(0, 100) + '...' : plainText;
              const isSelected = selectedPostId === result.id;

              return (
                <div
                  key={`${result.type}-${result.id}-${result.reply_id || ''}`}
                  onClick={() => handleSearchResultClick(result)}
                  className={`p-3 rounded cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-primary-100 dark:bg-primary-900 border-2 border-primary-600 dark:border-primary-400'
                      : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(result.created_at).toLocaleDateString()}
                    </div>
                    {result.type === 'reply' && (
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                        Reply
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-900 dark:text-gray-100">
                    {snippet}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PostsCalendarSearch;
